import { usePatientAllergies } from '@ctw/shared/api/fhir/allergies';
import type { AllergyModel } from '@ctw/shared/api/fhir/models/allergies';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { useMainContentHeader } from '@ctw/shared/components/layout/main-content-with-dockable-drawer';
import { patientAllergiesColumns } from '@ctw/shared/content/allergies/helpers/column';
import { EmptyPatientTable } from '@ctw/shared/content/empty-patients-table';
import { HotloadGate } from '@ctw/shared/content/hotload-gate';
import { ResourceTable } from '@ctw/shared/content/resource/resource-table';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useFilteredSortedData } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { capitalize } from 'lodash-es';
import { History } from '../resource/history';
import { ResourceTableActions } from '../resource/resource-table-actions';
import { useAllergiesHistory } from './helpers/history';
import { allergySortOptions, defaultAllergySort } from './helpers/sort';

export const allergyHistory = ({ model }: { model: AllergyModel }) => (
  <History getHistory={useAllergiesHistory} model={model} />
);

export const PatientAllergies = withErrorBoundary({
  boundaryName: 'PatientAllergies',
  Component: () => {
    const { patient } = usePatientContext();

    return (
      <HotloadGate upid={patient.UPID} resourceTypes={['AllergyIntolerance']}>
        <PatientAllergiesContent />
      </HotloadGate>
    );
  },
});

const PatientAllergiesContent = () => {
  const { requestContext } = useCTW();
  const { openResourceDrawer } = useResourceDrawer({ enableReadActions: true });
  const patientAllergiesQuery = usePatientAllergies();

  const { data, setSort, defaultSort } = useFilteredSortedData({
    cacheKey: 'patient-allergies',
    defaultSort: defaultAllergySort,
    records: patientAllergiesQuery.data,
  });
  const isEmptyQuery = patientAllergiesQuery.data?.length === 0;
  const hasZeroFilteredRecords = !isEmptyQuery && data.length === 0;

  useMainContentHeader(
    'PatientAllergiesContentHeader',
    () => (
      <ResourceTableActions
        sortOptions={{
          defaultSort,
          options: allergySortOptions,
          onChange: setSort,
        }}
      />
    ),
    [defaultSort, allergySortOptions],
  );

  return (
    <div>
      <ResourceTable
        title="Allergies"
        titleVisuallyHidden={true}
        showTableHead={true}
        isLoading={patientAllergiesQuery.isLoading}
        data={data}
        columns={patientAllergiesColumns(requestContext.builderId)}
        onRowClick={({ telemetry, record }) => openResourceDrawer({ telemetry, model: record })}
        enableReadActions={true}
        emptyMessage={
          <EmptyPatientTable
            hasZeroFilteredRecords={hasZeroFilteredRecords}
            resourceName="allergies"
          />
        }
      />
    </div>
  );
};

/** Builds definition list items for the allergy intolerance drawer. Data Source is shown only in history cards. */
export const allergyData = (allergy: AllergyModel, _builderId: string) => [
  { label: 'Recorded Date', value: allergy.recordedDate },
  { label: 'Recording Organization', value: allergy.patientOrganizationName },
  { label: 'Status', value: allergy.clinicalStatus },
  { label: 'Type', value: capitalize(allergy.type) },
  { label: 'Onset', value: allergy.onset },
  { label: 'Reaction', value: capitalize(allergy.manifestations) },
  { label: 'Severity', value: capitalize(allergy.severity) },
  { label: 'Note', value: allergy.note },
];
